import React from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Skeleton,
  IconButton,
} from "@mui/material";
import { Edit as EditIcon, RemoveRedEye as RemoveRedEyeIcon } from "@mui/icons-material";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";

import useRule from "../../../../hooks/super/useRule";
import useAuth from "../../../../hooks/useAuth";
import ViewCrsSecRuleModal from "./M_ViewCrsSecRule";
import EditCrsSecRuleModal from "./M_EditCrsSecRule";

import { IOSSwitch } from "../../../../components/pages/common/styled";
import { getComparator, stableSort } from "../../../../utils/tableSort";
import { CrsRuleNo, UserType } from "../../../../utils/constants";
import {
  TOKENS,
  EmptyState,
} from "../../../../components/pages/_new/super/design";

const headCells = [
  { id: "sec_rule_id", label: "Rule ID", sortable: true },
  { id: "description", label: "Description", sortable: true },
  { id: "tags", label: "Tags", sortable: false },
  { id: "enabled", label: "Enabled", sortable: true },
  { id: "action", label: "", sortable: false },
];

const HeaderCell = ({ children, sortable, id, orderBy, order, onSort, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "10px 14px",
      borderBottom: `1px solid ${TOKENS.border}`,
      background: TOKENS.bgSunken,
      color: TOKENS.fg2,
      fontSize: 11.5,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      minWidth: 100,
    }}
  >
    {sortable ? (
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : "asc"}
        onClick={() => onSort(id)}
        sx={{
          color: `${TOKENS.fg2} !important`,
          "& .MuiTableSortLabel-icon": { color: `${TOKENS.fg2} !important` },
        }}
      >
        {children}
      </TableSortLabel>
    ) : (
      children
    )}
  </TableCell>
);

const DataCell = ({ children, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "12px 14px",
      borderBottom: `1px solid ${TOKENS.borderSubtle}`,
      color: TOKENS.fg1,
      fontSize: 13.5,
    }}
  >
    {children}
  </TableCell>
);

function SecRuleTable({ disable }) {
  const { currule, configCrsSecRule } = useRule();
  const { adminUserType } = useAuth();
  const isAdminOrSuper = [UserType.SUPER_ADMIN, UserType.ADMIN].includes(adminUserType);

  const [rows, setRows] = React.useState(null);
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("sec_rule_id");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const handleSort = (id) => {
    if (id === orderBy) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setOrderBy(id);
      setOrder("asc");
    }
  };

  const [viewOpen, setViewOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [secRuleID, setSecRuleID] = React.useState();

  const viewClick = (id) => {
    setSecRuleID(id);
    setViewOpen(true);
  };
  const onEditClick = (id) => {
    setSecRuleID(id);
    setEditOpen(true);
  };

  const srcsecRuleChange = (event) => {
    let index = -1;
    for (let i = 0; i < rows?.length; i++) {
      if (rows[i]?.sec_rule_id.toString() === event.target.name) {
        index = i;
        break;
      }
    }
    if (index === -1) return;
    const value = {
      enable: !rows[index]?.enabled,
      rule_id: currule?.rule_id,
      sec_rule_id: rows[index]?.sec_rule_id.toString(),
    };
    const list = [...rows];
    list.splice(index, 1, {
      sec_rule_id: rows[index]?.sec_rule_id,
      enabled: !rows[index]?.enabled,
      description: rows[index]?.description,
      tags: rows[index]?.tags,
    });
    setRows(list);
    configCrsSecRule(value);
  };

  React.useEffect(() => {
    if (currule !== null && currule.length !== 0) {
      setRows(currule?.crs_sec_rules);
    } else if (currule?.length === 0) {
      setRows([]);
    } else {
      setRows(null);
    }
  }, [currule]);

  const sorted = React.useMemo(
    () => stableSort(Array.isArray(rows) ? rows : [], getComparator(order, orderBy)),
    [rows, order, orderBy]
  );
  const isLoading = rows === null;
  const isEmpty = !isLoading && sorted.length === 0;
  const count = Array.isArray(rows) ? rows.length : 0;
  const last = Math.max(0, Math.ceil(count / rowsPerPage) - 1);

  const paged = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box
      sx={{
        border: `1px solid ${TOKENS.border}`,
        borderRadius: `${TOKENS.radiusM}px`,
        background: TOKENS.bgSurface,
        boxShadow: TOKENS.shadowXs,
        overflow: "hidden",
      }}
    >
      <TableContainer>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              {headCells.map((c) => (
                <HeaderCell
                  key={c.id}
                  id={c.id}
                  sortable={c.sortable}
                  orderBy={orderBy}
                  order={order}
                  onSort={handleSort}
                  align={c.id === "action" ? "right" : "left"}
                >
                  {c.label}
                </HeaderCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: rowsPerPage }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  {headCells.map((__, j) => (
                    <DataCell key={`sk-${i}-${j}`}>
                      <Skeleton variant="rectangular" height={18} sx={{ borderRadius: 1 }} />
                    </DataCell>
                  ))}
                </TableRow>
              ))
            ) : isEmpty ? (
              <TableRow>
                <TableCell colSpan={headCells.length} sx={{ borderBottom: "none" }}>
                  <EmptyState
                    icon={<RuleOutlinedIcon />}
                    title="No SD signature rules"
                    body="Pick a different category or wait for rules to be loaded."
                  />
                </TableCell>
              </TableRow>
            ) : (
              paged.map((row, index) => (
                <TableRow
                  key={`${row?.sec_rule_id}-${index}`}
                  hover
                  sx={{ "&:hover": { background: TOKENS.bgSunken } }}
                >
                  <DataCell>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: TOKENS.fg1,
                        fontWeight: 600,
                        fontFamily:
                          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                      }}
                    >
                      {row?.sec_rule_id}
                    </Typography>
                  </DataCell>
                  <DataCell>
                    <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>
                      {row?.description || "—"}
                    </Typography>
                  </DataCell>
                  <DataCell>
                    {row?.tags?.length ? (
                      <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
                        {row.tags.map((tag, i) => (
                          <Typography
                            key={i}
                            component="span"
                            sx={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: TOKENS.primaryInk,
                              background: TOKENS.primaryTint,
                              border: `1px solid ${TOKENS.borderSubtle}`,
                              px: "8px",
                              py: "2px",
                              borderRadius: 999,
                            }}
                          >
                            {tag}
                          </Typography>
                        ))}
                      </Stack>
                    ) : (
                      <Typography sx={{ fontSize: 13, color: TOKENS.fg3 }}>—</Typography>
                    )}
                  </DataCell>
                  <DataCell>
                    <IOSSwitch
                      disabled={!isAdminOrSuper || disable}
                      checked={!!row?.enabled}
                      name={row?.sec_rule_id}
                      onChange={srcsecRuleChange}
                    />
                  </DataCell>
                  <DataCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => viewClick(row?.sec_rule_id)}
                      disabled={parseInt(currule?.rule_id) === CrsRuleNo.CUSTOM}
                      sx={{ color: TOKENS.fg2 }}
                    >
                      <RemoveRedEyeIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => onEditClick(row?.sec_rule_id)}
                      disabled={
                        !isAdminOrSuper || parseInt(currule?.rule_id) === CrsRuleNo.CUSTOM
                      }
                      sx={{ color: TOKENS.fg2 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </DataCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!isLoading && count > 0 ? (
        <Box
          sx={{
            p: "10px 16px",
            borderTop: `1px solid ${TOKENS.borderSubtle}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2 }}>
            Page {page + 1} of {Math.max(1, last + 1)}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography
            component="button"
            type="button"
            onClick={() => page > 0 && setPage(page - 1)}
            disabled={page === 0}
            sx={{
              border: `1px solid ${TOKENS.border}`,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.bgSurface,
              color: page === 0 ? TOKENS.fg3 : TOKENS.fg1,
              fontSize: 12.5,
              fontWeight: 600,
              px: 1.5,
              py: 0.5,
              cursor: page === 0 ? "not-allowed" : "pointer",
            }}
          >
            Prev
          </Typography>
          <Typography
            component="button"
            type="button"
            onClick={() => page < last && setPage(page + 1)}
            disabled={page >= last}
            sx={{
              border: `1px solid ${TOKENS.border}`,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.bgSurface,
              color: page >= last ? TOKENS.fg3 : TOKENS.fg1,
              fontSize: 12.5,
              fontWeight: 600,
              px: 1.5,
              py: 0.5,
              cursor: page >= last ? "not-allowed" : "pointer",
            }}
          >
            Next
          </Typography>
          <Box
            component="select"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              border: `1px solid ${TOKENS.border}`,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.bgSurface,
              color: TOKENS.fg1,
              fontSize: 12.5,
              px: 1,
              py: 0.5,
            }}
          >
            <option value={5}>5 / page</option>
            <option value={10}>10 / page</option>
            <option value={25}>25 / page</option>
          </Box>
        </Box>
      ) : null}

      <ViewCrsSecRuleModal open={viewOpen} handleClose={() => setViewOpen(false)} secRuleID={secRuleID} />
      <EditCrsSecRuleModal open={editOpen} handleClose={() => setEditOpen(false)} ruleID={secRuleID} />
    </Box>
  );
}

export default SecRuleTable;
